import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from './Button';

const MessageModal = ({
  isOpen,
  initialValue = '',
  maxLength = 100,
  onSave,
  onDelete,
  onClose
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialValue);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) setDraft(initialValue);
  }, [isOpen, initialValue]);

  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const textarea = modalRef.current?.querySelector('textarea');
    if (textarea) textarea.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
      if (previousFocusRef.current) previousFocusRef.current.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasExisting = !!initialValue;

  return (
    <div
      className="msgModalOverlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="msgModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="msg-modal-title"
        ref={modalRef}
        tabIndex="-1"
      >
        <div className="msgModalHead">
          <h2 id="msg-modal-title">{t('Message personnalisé')}</h2>
          <button
            type="button"
            className="msgModalClose"
            onClick={onClose}
            aria-label={t('Fermer')}
          >
            &times;
          </button>
        </div>

        <div className="msgModalField">
          <div className="msgModalFieldMeta">
            <label htmlFor="customMsg">{t('Message (facultatif)')}</label>
            <span className="msgCounter">{draft.length}/{maxLength}</span>
          </div>
          <textarea
            id="customMsg"
            name="customMsg"
            rows="4"
            maxLength={maxLength}
            placeholder={t('Renseignez un message personnalisé (facultatif)')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          ></textarea>
        </div>

        <p className="msgModalHint">
          {t("Votre message ne doit pas contenir de propos injurieux ou considérés comme sensibles (ex : origine raciale ou ethnique, opinions religieuses, données de santé ou relative à la vie sexuelle)")}
        </p>

        <Button
          className="msgModalSave"
          disabled={!draft.trim()}
          onClick={() => onSave(draft)}
        >
          {t('Sauvegarder')}
        </Button>

        {hasExisting ? (
          <button type="button" className="msgModalLink" onClick={onDelete}>
            {t('Supprimer')}
          </button>
        ) : (
          <button type="button" className="msgModalLink" onClick={onClose}>
            {t('Annuler')}
          </button>
        )}
      </div>
    </div>
  );
};

MessageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialValue: PropTypes.string,
  maxLength: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default MessageModal;
